"use strict"

import React from "react"
import { Button, Form, Modal } from "react-bootstrap"
import { FormattedMessage } from "react-intl"
import { Formik } from "formik"
import * as Yup from "yup"

import ReactSelectionElement from "../../../shared_components/widgets/ReactSelectionElement"
import { stringifyGroupName } from "../../../shared_components/utils"
import UserInputField from "./UserInputField"

const UserCheckboxField = ({
  errors,
  fieldname,
  handleBlur,
  handleChange,
  touched,
  values,
  translations_key,
  show_help,
  intl,
}) => (
  <div className={"row one-rem-margin-bottom"}>
    <div className={"col-12"}>
      <Form.Check
        type="checkbox"
        name={fieldname}
        onBlur={handleBlur}
        onChange={handleChange}
        checked={values[fieldname] || false}
        value={values[fieldname] || false}
        label={intl.formatMessage({
          id: `administration.user-management.${translations_key}.${fieldname}`,
        })}
      />
      {touched[fieldname] && errors[fieldname] ? (
        <div className="error-message">{errors[fieldname]}</div>
      ) : null}
      {show_help && (
        <Form.Text className="text-muted">
          {intl.formatMessage({
            id: `administration.user-management.${translations_key}.${fieldname}.help_text`,
          })}
        </Form.Text>
      )}
    </div>
  </div>
)

const GroupsSection = (props) => (
  <UserCheckboxField
    {...props}
    fieldname={stringifyGroupName(props.group)}
    translations_key={"groups"}
    show_help={false}
  />
)

export const UserModal = ({
  enterUserModal,
  groups,
  interfaces = [],
  intl,
  onHide,
  selectedUser,
  submitUserData,
  isAddingNewUser,
}) => {
  const validationSchema = Yup.object().shape({
    username: Yup.string().required(intl.formatMessage({ id: "general.warning.requiredFormField" })),
    email: Yup.string()
      .email(intl.formatMessage({ id: "administration.user-management.email.valid" }))
      .required(intl.formatMessage({ id: "general.warning.requiredFormField" })),
    first_name: Yup.string().required(intl.formatMessage({ id: "general.warning.requiredFormField" })),
    last_name: Yup.string().required(intl.formatMessage({ id: "general.warning.requiredFormField" })),
    is_active: Yup.boolean(),
    is_staff: Yup.boolean(),
    is_superuser: Yup.boolean(),
    is_system_user: Yup.boolean(),
    is_file_uploader: Yup.boolean(),
    is_fermentation_operator: Yup.boolean(),
    is_fermentation_manager: Yup.boolean(),
    is_system_administrator: Yup.boolean(),
    is_analytics_scientist: Yup.boolean(),
    is_analytics_manager: Yup.boolean(),
    is_user_account_manager: Yup.boolean(),
    is_graph_config_editor: Yup.boolean(),
    password: Yup.string().concat(
      isAddingNewUser
        ? Yup.string()
            .required(intl.formatMessage({ id: "general.warning.requiredFormField" }))
            .min(8, intl.formatMessage({ id: "administration-user-management.password.minimum" }))
        : null,
    ),
    confirmPassword: Yup.string()
      .when("password", (password, schema) => {
        if (isAddingNewUser)
          return schema.required(intl.formatMessage({ id: "general.warning.requiredFormField" }))
      })
      .oneOf(
        [Yup.ref("password")],
        // "Passwords must match"
        intl.formatMessage({ id: "administration.user-management.confirmPassword.match" }),
      ),
  })

  const userGroupProps = []
  let value
  groups.map((grp) => {
    value = selectedUser?.group_names?.includes(grp.name)
    userGroupProps[stringifyGroupName(grp)] = value
  })

  return (
    <Modal
      size={"lg"}
      dialogClassName={"common-modal"}
      user={selectedUser}
      show={enterUserModal}
      onHide={onHide}
      style={{ overflowX: "scroll", overflowY: "scroll" }}
      backdrop={"static"}
      keyboard={false}
    >
      <Formik
        initialValues={{
          username: selectedUser.username,
          email: selectedUser.email,
          first_name: selectedUser.first_name,
          last_name: selectedUser.last_name,
          is_active: selectedUser.is_active,
          is_staff: selectedUser.is_staff,
          is_superuser: selectedUser.is_superuser,
          is_system_user: selectedUser.is_system_user,
          is_file_uploader: selectedUser.is_file_uploader,
          is_graph_config_editor: selectedUser.is_graph_config_editor,
          user_graph_interfaces: selectedUser.user_graph_interfaces,
          [isAddingNewUser ? "password" : ""]: "",
          [isAddingNewUser ? "confirmPassword" : ""]: "",
          ...userGroupProps,
        }}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting, resetForm }) => {
          setSubmitting(true)
          setTimeout(() => {
            submitUserData(selectedUser, values)
            resetForm()
            setSubmitting(false)
            onHide()
          }, 500)
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, isValid }) => (
          <Form onSubmit={handleSubmit}>
            <Modal.Header closeButton>
              <Modal.Title>
                <FormattedMessage id={"administration.user-management.edit.user"} description={"Edit User"} />
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <>
                {/*---------- Login Info Heading and Section ----------*/}
                <h2 className="user-form-section-heading">Login info</h2>
                <hr />
                {["username", "email"].map((field, idx) => (
                  <React.Fragment key={field + idx}>
                    <UserInputField
                      intl={intl}
                      values={values}
                      controlName={field}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      touched={touched}
                      errors={errors}
                      type={"text"}
                      show_complexity_help={true}
                    />
                  </React.Fragment>
                ))}

                {isAddingNewUser && (
                  <>
                    {/*---------- Password Heading and Section ----------*/}
                    <h2 className="user-form-section-heading">Password info</h2>
                    <hr />
                    {["password", "confirmPassword"].map((field, idx) => (
                      <React.Fragment key={field + idx}>
                        <UserInputField
                          intl={intl}
                          values={values}
                          controlName={field}
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          touched={touched}
                          errors={errors}
                          type={"password"}
                          show_complexity_help={true}
                        />
                      </React.Fragment>
                    ))}
                  </>
                )}

                {/*---------- Personal Info Heading and Section----------*/}
                <h2 className="user-form-section-heading">
                  {intl.formatMessage({ id: "administration.user-management.personal.info.heading" })}
                </h2>
                <hr />
                {["first_name", "last_name"].map((fieldname, idx) => (
                  <React.Fragment key={fieldname + idx}>
                    <UserInputField
                      intl={intl}
                      values={values}
                      controlName={fieldname}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      touched={touched}
                      errors={errors}
                      type={"text"}
                      show_complexity_help={false}
                    />
                  </React.Fragment>
                ))}

                {/*---------- Permissions Heading and Section ----------*/}
                <h2 className="user-form-section-heading">
                  {intl.formatMessage({ id: "administration.user-management.permissions" })}
                </h2>
                <hr />

                {[
                  "is_active",
                  "is_staff",
                  "is_superuser",
                  "is_system_user",
                  "is_file_uploader",
                  "is_graph_config_editor",
                ].map((fieldname, idx) => (
                  <React.Fragment key={fieldname + idx}>
                    <UserCheckboxField
                      errors={errors}
                      fieldname={fieldname}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                      intl={intl}
                      touched={touched}
                      values={values}
                      translations_key={"permissions"}
                      show_help={true}
                    />
                  </React.Fragment>
                ))}

                {/*---------- Permissions Heading and Section ----------*/}
                {values.is_graph_config_editor && (
                  <>
                    <h2 className="user-form-section-heading">
                      {intl.formatMessage({
                        id: "administration.user-management.graph_interfaces",
                        defaultMessage: "Graphing Pages",
                      })}
                    </h2>
                    <hr />
                    <ReactSelectionElement
                      name={"user_graph_interfaces"}
                      list={interfaces.map((s) => {
                        return { value: s }
                      })}
                      onValueChange={(_, selectedInterfaces) =>
                        (values.user_graph_interfaces = selectedInterfaces.map((s) => s.value))
                      }
                      defaultValue={(values.user_graph_interfaces || []).map((s) => {
                        return { value: s }
                      })}
                    />
                  </>
                )}

                {/*---------- Groups Heading and Section ----------*/}
                <h2 className="user-form-section-heading">
                  {intl.formatMessage({ id: "administration.user-management.groups" })}
                </h2>
                <hr />

                {groups.map((group, idx) => (
                  <React.Fragment key={group + idx}>
                    <GroupsSection
                      errors={errors}
                      group={group}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                      idx={idx}
                      intl={intl}
                      touched={touched}
                      values={values}
                    />
                  </React.Fragment>
                ))}
              </>
            </Modal.Body>
            <Modal.Footer>
              <Button variant={"outline-primary"} onClick={onHide}>
                <FormattedMessage id={"general.cancelButton"} description={"Cancel"} />
              </Button>
              <Button variant={"primary"} type={"submit"} disabled={!isValid}>
                <FormattedMessage
                  id={"general.submitButton"}
                  description={"Submit Button For Edit User Form"}
                />
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}
